import { useCallback, useMemo } from 'react';

import { InlineAlert } from '@rbilabs/universal-components';
import { spacing } from '@rbilabs/universal-components/themes';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { FlatList, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { CardModalHeader } from 'components/card-modal-header';
import {
  CenteredRowContainer,
  centeredRowContainerStyles,
} from 'components/layout/centered-row-container';
import { useModalAnalyticsAppearanceEvent } from 'hooks/use-modal-analytics-appearance-event';
import { selectors, useAppSelector } from 'state/global-state';
import { t } from 'state/intl/translations';
import { useLoyaltyContext } from 'state/loyalty';
import { useLoyaltyOfferGroupsById } from 'state/loyalty/hooks/use-loyalty-offer-groups-by-id';
import { AppOffer, SanityId } from 'state/loyalty/types';
import { WEB_SMALL_MAX_WIDTH } from 'utils/constants';
import { routes } from 'utils/routing';
import { blockContentToPlainText } from 'utils/sanity';

import { LoadingAnimation } from '../loyalty-claim-points/loyalty-claim-points.styled';

import { renderOfferItem } from './loyalty-list-item';

export const OffersGroupsDetails: React.FC = () => {
  const { offerGroupId } = useLocalSearchParams<{ offerGroupId?: SanityId }>();

  const { setAddOffersCallbackUrl } = useLoyaltyContext();
  // This screen renders the "offers inside a group" UI.
  //
  // There are 2 kinds of groups a user can tap from the Offers list:
  // - Systemwide Offer Group: a CMS-backed container that can be fetched by `cmsId`.
  // - Personalized Offer Group: an engine-assigned container whose header *may* be derived
  //   from a source CMS Offer Group, but whose *child offers* come from the user's
  //   personalized assignment.
  //
  // Both navigate here using `offerGroupId` in the route, so this screen chooses the
  // correct data source based on what is available in Redux.
  const appPersonalizedOfferGroups = useAppSelector(
    selectors.loyalty.selectAppPersonalizedOfferGroups
  );
  const personalizedOfferGroup = useMemo(() => {
    if (!offerGroupId) {
      return undefined;
    }
    // Route params are strings; our IDs are branded types, so normalize before comparing.
    return appPersonalizedOfferGroups.find(group => String(group.cmsId) === String(offerGroupId));
  }, [appPersonalizedOfferGroups, offerGroupId]);

  const {
    offers: systemwideOffers,
    groupName: systemwideGroupName,
    canOnlyChooseOne: systemwideCanOnlyChooseOne,
    loading: systemwideOffersLoading,
    // If Redux already contains a matching Personalized Offer Group, we skip fetching the
    // systemwide group. That ensures we show the user's assigned offers, not the source
    // group's default offers. We still call the hook to satisfy the Rules of Hooks;
    // passing `undefined` is the hook's "do nothing" path.
  } = useLoyaltyOfferGroupsById(personalizedOfferGroup ? undefined : offerGroupId);

  // The list UI can render both offer types using `renderOfferItem`, so we unify the
  // source as `AppOffer[]`.
  const offers = (personalizedOfferGroup?.offers ?? systemwideOffers) as AppOffer[];
  const groupName = personalizedOfferGroup?.name
    ? blockContentToPlainText(personalizedOfferGroup.name?.localeRaw)
    : systemwideGroupName;
  const canOnlyChooseOne = personalizedOfferGroup?.canOnlyChooseOne ?? systemwideCanOnlyChooseOne;
  const offersLoading = personalizedOfferGroup ? false : systemwideOffersLoading;
  const router = useRouter();

  const handleOfferPress = useCallback(
    (incentive: AppOffer) => {
      const incentiveId = incentive.loyaltyEngineId;
      const qs = offerGroupId ? `?groupId=${offerGroupId}` : '';
      router.navigate(`${routes.loyaltyOfferList}/${incentiveId}${qs}`);
    },
    [router, offerGroupId]
  );

  const renderItem = ({ item }: { item: AppOffer }) => {
    return renderOfferItem({
      item,
      handlePress: () => {
        if (item?.inlineAlertMessage) {
          return;
        }
        setAddOffersCallbackUrl(`${routes.loyaltyOfferGroup}/${offerGroupId}`);
        handleOfferPress(item);
      },
      skipAnimation: true,
    });
  };
  useModalAnalyticsAppearanceEvent('Offers Group Details');

  const { top } = useSafeAreaInsets();
  const headerText = groupName || t('selectOffer');

  return (
    <View testID="offers-group-items" style={{ flex: 1, paddingTop: top - 16 }}>
      <CenteredRowContainer standardPadding>
        <CardModalHeader
          {...{
            headerText,
            dismissType: 'back',
            containerStyle: { borderTopLeftRadius: 0, borderTopRightRadius: 0 },
          }}
        />
      </CenteredRowContainer>
      {canOnlyChooseOne && (
        <InlineAlert
          status="info"
          marginBottom={'$4'}
          message={
            <>
              {t('canOnlyChooseOneOfferLine1')}
              {'\n'}
              {t('canOnlyChooseOneOfferLine2')}
            </>
          }
        />
      )}
      {offersLoading ? (
        <LoadingAnimation />
      ) : (
        <FlatList
          data={offers}
          contentContainerStyle={styles.flatList}
          keyExtractor={(item, index) => item.cmsId || item.loyaltyEngineId || `${index}`}
          renderItem={renderItem}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  flatList: {
    ...centeredRowContainerStyles.content,
    maxWidth: WEB_SMALL_MAX_WIDTH,
    paddingHorizontal: spacing.$4,
    paddingTop: spacing.$4,
  },
});
